import { tracePhase } from '../lib/perfProbe'
import { ARKIT_NAMES } from '../lib/arkitNames'

// Re-exported so existing importers keep their import path. The list itself
// lives in ../lib/arkitNames so pure-logic modules can read it without this
// file's network/perf-probe dependencies.
export { ARKIT_NAMES }

/**
 * ARKit name → MetaHuman GLB morph target name.
 * GLB keys use format: head_lod0_mesh__<shape_name>
 * Derived from MetaHuman FBX export + morph target dictionary inspection.
 */
const mh = name => `head_lod0_mesh__${name}`

const ARKIT_TO_METAHUMAN = {
  // Eye — blink
  EyeBlinkLeft:         mh('eye_blink_L'),
  EyeBlinkRight:        mh('eye_blink_R'),
  // Eye — look directions
  EyeLookDownLeft:      mh('eye_lookDown_L'),
  EyeLookDownRight:     mh('eye_lookDown_R'),
  EyeLookUpLeft:        mh('eye_lookUp_L'),
  EyeLookUpRight:       mh('eye_lookUp_R'),
  EyeLookInLeft:        mh('eye_lookRight_L'),   // in = toward nose = rightward for left eye
  EyeLookInRight:       mh('eye_lookLeft_R'),    // in = toward nose = leftward for right eye
  EyeLookOutLeft:       mh('eye_lookLeft_L'),    // out = away from nose = leftward for left eye
  EyeLookOutRight:      mh('eye_lookRight_R'),   // out = away from nose = rightward for right eye
  // Eye — squint / wide
  EyeSquintLeft:        mh('eye_squintInner_L'),
  EyeSquintRight:       mh('eye_squintInner_R'),
  EyeWideLeft:          mh('eye_widen_L'),
  EyeWideRight:         mh('eye_widen_R'),
  // Jaw
  JawForward:           mh('jaw_left'),  // Substitute: jaw_forward missing, use jaw_left as proxy
  JawLeft:              mh('jaw_left'),
  JawRight:             mh('jaw_right'),
  JawOpen:              mh('jaw_open'),
  // Mouth — lips close / funnel / pucker
  MouthClose:           mh('mouth_lipsTowards_UL'),
  MouthFunnel:          mh('mouth_funnel_DL'),
  MouthPucker:          mh('mouth_lipsPurse_UL'),
  // Mouth — lateral
  MouthLeft:            mh('mouth_left'),
  MouthRight:           mh('mouth_right'),
  // Mouth — smile / frown
  MouthSmileLeft:       mh('mouth_cornerPull_left'),
  MouthSmileRight:      mh('mouth_cornerPull_right'),
  MouthFrownLeft:       mh('mouth_cornerDepress_L'),
  MouthFrownRight:      mh('mouth_cornerDepress_R'),
  // Mouth — dimple / stretch
  MouthDimpleLeft:      mh('mouth_dimple_left'),
  MouthDimpleRight:     mh('mouth_dimple_right'),
  MouthStretchLeft:     mh('mouth_stretch_left'),
  MouthStretchRight:    mh('mouth_stretch_right'),
  // Mouth — roll / shrug
  MouthRollLower:       mh('mouth_down'),  // Substitute: mouth_rollLower missing, use mouth_down
  MouthRollUpper:       mh('mouth_up'),    // Substitute: mouth_rollUpper missing, use mouth_up
  MouthShrugLower:      mh('mouth_down'),
  MouthShrugUpper:      mh('mouth_up'),
  // Mouth — press / lip raise / lip depress
  MouthPressLeft:       mh('mouth_press_UL'),
  MouthPressRight:      mh('mouth_press_UR'),
  MouthLowerDownLeft:   mh('mouth_lowerLipDepress_left'),
  MouthLowerDownRight:  mh('mouth_lowerLipDepress_right'),
  MouthUpperUpLeft:     mh('mouth_upperLipRaise_left'),
  MouthUpperUpRight:    mh('mouth_upperLipRaise_right'),
  // Brow
  BrowDownLeft:         mh('brow_down_L'),
  BrowDownRight:        mh('brow_down_R'),
  BrowInnerUp:          mh('brow_raiseIn_L'),
  BrowOuterUpLeft:      mh('brow_raiseOuter_left'),
  BrowOuterUpRight:     mh('brow_raiseOuter_right'),
  // Cheek
  CheekPuff:            mh('cheek_blow_cor'),
  CheekSquintLeft:      mh('eye_cheekRaise_L'),
  CheekSquintRight:     mh('eye_cheekRaise_R'),
  // Nose
  NoseSneerLeft:        mh('nose_wrinkle_left'),
  NoseSneerRight:       mh('nose_wrinkle_right'),
  // Tongue
  TongueOut:            mh('mouth_open'),  // Substitute: tongue_out missing, use mouth_open
}

// Module-level "current frame" exposure for consumers that don't own a
// buffer reference (e.g. plugin host imports). Written every time a buffer
// advances; reused Float32Array(52) so callers must not retain it across
// frames. Returns null if no frame has played yet.
const _currentArkit = new Float32Array(52)
let _currentArkitValid = false

// Debug forcing: when set, getCurrentArkit() returns this array instead
// of the live A2F stream. Lets us pose-test the avatar (force a jaw-open,
// a smile, etc.) without needing the mic or a Convorcher response.
// Set via window.__FORCE_ARKIT__(values) — see below.
let _forcedArkit = null

export function getCurrentArkit() {
  if (_forcedArkit) return _forcedArkit
  return _currentArkitValid ? _currentArkit : null
}

export function isArkitForced() {
  return _forcedArkit !== null
}

/**
 * Force the ARKit stream to a fixed pose for testing. Accepts:
 *   - Float32Array(52) — used directly
 *   - object — keyed by ARKit name (camelCase: 'jawOpen', 'mouthSmileLeft', …).
 *     Unspecified slots default to 0.
 *   - 'aaah' | 'smile' | 'blink' | 'rest' — built-in presets
 *   - null / undefined — disable forcing, resume live stream
 * After calling, the next call to getCurrentArkit() returns the forced
 * value. All consumers (LiveLink morph path + rig2mesh plugin) see the
 * same forced data, so the avatar visually reflects the pose.
 */
export function setForcedArkit(values) {
  if (values == null) { _forcedArkit = null; return null }
  if (values instanceof Float32Array) {
    _forcedArkit = values.length === 52 ? values : null
    return _forcedArkit
  }
  if (typeof values === 'string') {
    const presets = {
      rest:  {},
      aaah:  { jawOpen: 0.9 },
      smile: { mouthSmileLeft: 0.7, mouthSmileRight: 0.7 },
      blink: { eyeBlinkLeft: 1.0, eyeBlinkRight: 1.0 },
      pucker: { mouthPucker: 0.8 },
      surprise: { jawOpen: 0.5, mouthFunnel: 0.3, eyeWideLeft: 0.6, eyeWideRight: 0.6 },
    }
    values = presets[values] ?? {}
  }
  const arr = new Float32Array(52)
  for (let i = 0; i < ARKIT_NAMES.length; i++) {
    const pascal = ARKIT_NAMES[i]
    const camel  = pascal.charAt(0).toLowerCase() + pascal.slice(1)
    if (values[camel]  !== undefined) arr[i] = values[camel]
    else if (values[pascal] !== undefined) arr[i] = values[pascal]
  }
  _forcedArkit = arr
  return arr
}

if (typeof window !== 'undefined') {
  window.__FORCE_ARKIT__ = setForcedArkit
  window.__ARKIT_NAMES__ = ARKIT_NAMES
  // The live ARKit frame consumed by lip-sync (LiveLink morphs + Isaac splat
  // rig). Exposed for tests + console inspection of the A2F stream.
  window.__GET_ARKIT__ = getCurrentArkit
}

export class AudioAnimationBuffer {
  constructor() {
    this.frames = []
    this.frameIndex = 0
    this.totalReceived = 0
    this.sentenceNum = -1
    this._mapping = null
    this._mappedCount = 0
    this._playbackStartTime = null  // performance.now() when first frame was applied
    this._firstFrameTimeCode = 0    // timeCode of the first frame in the current sequence
    this._completeFired = false     // guard: fire onComplete only once per sequence
    this.onComplete = null          // () => void — called when all frames have played
  }

  /**
   * Build ARKit → GLB index mapping from the face mesh's morphTargetDictionary.
   * Call once when the face mesh is ready.
   */
  buildMapping(morphTargetDictionary) {
    if (!morphTargetDictionary) {
      console.warn('[AudioAnimationBuffer] No morphTargetDictionary — mapping not built')
      return
    }

    const allKeys = Object.keys(morphTargetDictionary)

    this._mapping = ARKIT_NAMES.map(arkitName => {
      // Try three naming conventions in order:
      //  1. MetaHuman FBX export (`head_lod0_mesh__eye_blink_L`) — original rig
      //  2. v002 arkit rig (`myra_head_arkit_blendShape.eyeBlinkLeft`)
      //  3. Bare camelCase ARKit (`eyeBlinkLeft`) — current ARKit_rig_003 export
      const mhName      = ARKIT_TO_METAHUMAN[arkitName]
      const camel       = arkitName.charAt(0).toLowerCase() + arkitName.slice(1)
      const altName     = `myra_head_arkit_blendShape.${camel}`
      const idx =
        (mhName   !== undefined ? morphTargetDictionary[mhName]   : undefined) ??
        morphTargetDictionary[altName] ??
        morphTargetDictionary[camel]
      if (idx === undefined) {
        console.log(`[AudioAnimationBuffer] Missing: ARKit "${arkitName}" → tried "${mhName}", "${altName}", "${camel}"`)
      }
      return idx !== undefined ? idx : -1
    })

    this._mappedCount = this._mapping.filter(i => i !== -1).length
    const missing = ARKIT_NAMES.filter((name, i) => this._mapping[i] === -1)
    if (missing.length > 0) {
      console.warn('[AudioAnimationBuffer] Unmapped ARKit names:', missing)
    }
  }

  push(msg) {
    tracePhase(`aab.push.${msg.frames?.length ?? 0}f`, () =>
      this._addFrames(msg.frames, msg.sentenceNum ?? -1))
  }

  pushStreamChunk(msg) {
    tracePhase(`aab.pushStreamChunk.${msg.frames?.length ?? 0}f`, () =>
      this._addFrames(msg.frames, msg.sentenceNum ?? -1))
  }

  _addFrames(frames, sentenceNum) {
    if (!frames || frames.length === 0) return
    this.sentenceNum = sentenceNum
    for (const frame of frames) this.frames.push(frame)
    this.totalReceived += frames.length
  }

  getCurrentWeights() {
    if (this.frames.length === 0) return null
    const frame = this.frames[this.frameIndex]
    const w = frame?.blendshapeWeights ?? null
    if (w) {
      // Mirror into the module-level Float32Array for getCurrentArkit()
      // consumers (plugin host imports, etc.). Server sends a regular
      // Array<number>; we keep a typed copy so callers don't pay the
      // unboxing cost on every read.
      const n = Math.min(w.length, _currentArkit.length)
      for (let i = 0; i < n; i++) _currentArkit[i] = w[i]
      _currentArkitValid = true
    }
    return w
  }

  advanceFrame() {
    if (this.frames.length === 0) return
    const now = performance.now()
    if (this._playbackStartTime === null) {
      this._playbackStartTime = now
      this._firstFrameTimeCode = this.frames[0]?.timeCode ?? 0
    }
    const elapsed = now - this._playbackStartTime
    while (
      this.frameIndex < this.frames.length - 1 &&
      (this.frames[this.frameIndex + 1].timeCode - this._firstFrameTimeCode) * 1000 <= elapsed
    ) {
      this.frameIndex++
    }
  }

  applyToMesh(skinnedMesh, masterWeight = 1.0) {
    this.applyToMeshes(skinnedMesh ? [skinnedMesh] : [], masterWeight)
  }

  /**
   * Advance the playback cursor by wall-clock time and mirror the current
   * frame's 52 ARKit weights into the module-level _currentArkit (the
   * getCurrentArkit() source). Mesh-free: splat avatars (OGSModel) drive
   * their rig from getCurrentArkit() directly and have no morph meshes for
   * applyToMeshes to walk, so they call this every frame to keep the live
   * stream advancing. Also the shared engine behind applyToMeshes.
   *
   * @returns {number[]|Float32Array|null} the current frame's weights (or the
   *   forced pose), or null when there's nothing playing.
   */
  advancePlayback() {
    // Debug-forced pose path: getCurrentArkit() already returns _forcedArkit
    // directly, so there's nothing to advance — just report it.
    if (_forcedArkit) return _forcedArkit
    if (this.frames.length === 0) return null

    // Advance frameIndex based on elapsed wall-clock time vs frame timeCodes
    const now = performance.now()
    if (this._playbackStartTime === null) {
      this._playbackStartTime = now
      this._firstFrameTimeCode = this.frames[0]?.timeCode ?? 0
    }
    const elapsed = now - this._playbackStartTime
    // Seek to the latest frame whose timeCode (in seconds) * 1000 <= elapsed (ms)
    while (
      this.frameIndex < this.frames.length - 1 &&
      (this.frames[this.frameIndex + 1].timeCode - this._firstFrameTimeCode) * 1000 <= elapsed
    ) {
      this.frameIndex++
    }

    // Fire onComplete once when the last frame has been reached and its time has elapsed
    if (!this._completeFired && this.frameIndex >= this.frames.length - 1) {
      const lastFrame = this.frames[this.frames.length - 1]
      const lastElapsed = (lastFrame?.timeCode ?? 0) - this._firstFrameTimeCode
      if (elapsed >= lastElapsed * 1000) {
        this._completeFired = true
        this.onComplete?.()
      }
    }

    const frame = this.frames[this.frameIndex]
    if (!frame) return null

    const weights = frame.blendshapeWeights || []
    // Mirror into the module-level _currentArkit Float32Array for plugin
    // host imports (host.getArkit()) — the canonical "the avatar is using
    // this frame right now" hook.
    if (weights.length) {
      const n = Math.min(weights.length, _currentArkit.length)
      for (let i = 0; i < n; i++) _currentArkit[i] = weights[i]
      _currentArkitValid = true
    }
    return weights.length ? weights : null
  }

  applyToMeshes(meshes, masterWeight = 1.0) {
    // Debug-forced pose path: when _forcedArkit is set, drive morphs from
    // it directly. Bypasses the frame queue entirely so callers can pose
    // the avatar without a Convorcher response feeding the buffer.
    if (_forcedArkit) {
      if (meshes && meshes.length) this._applyWeights(meshes, _forcedArkit, masterWeight)
      return
    }
    // Advance the cursor + mirror _currentArkit, then paint the morphs. Done
    // unconditionally (even with no meshes) so the live stream keeps moving.
    const weights = this.advancePlayback()
    if (weights && meshes && meshes.length) this._applyWeights(meshes, weights, masterWeight)
  }

  // Shared morph application loop used by both the per-frame playback
  // path (above) and the debug-forced pose path. `weights` is a 52-element
  // ARKit-ordered array (Array or Float32Array); `masterWeight` scales
  // each influence on top of the per-shape AMPLIFICATION constant.
  _applyWeights(meshes, weights, masterWeight) {
    const AMPLIFICATION = 1.2  // Slightly boost subtle MetaHuman corrective shapes
    for (const mesh of meshes) {
      const influences = mesh.morphTargetInfluences
      if (!influences || influences.length === 0) continue

      if (this._mapping) {
        for (let i = 0; i < weights.length && i < this._mapping.length; i++) {
          const glbIdx = this._mapping[i]
          if (glbIdx !== -1) influences[glbIdx] = Math.min(weights[i] * AMPLIFICATION * masterWeight, 1.0)
        }
      } else {
        const limit = Math.min(weights.length, influences.length)
        for (let i = 0; i < limit; i++) influences[i] = Math.min(weights[i] * AMPLIFICATION * masterWeight, 1.0)
      }
    }
  }

  clear() {
    this.frames = []
    this.frameIndex = 0
    this.sentenceNum = -1
    this._playbackStartTime = null
    this._firstFrameTimeCode = 0
    this._completeFired = false
    this.onComplete = null
    // Invalidate the module-level mirror so plugins fall back to neutral
    // between sentences instead of holding the last frozen expression.
    _currentArkitValid = false
  }

  get stats() {
    return {
      buffered: this.frames.length,
      played: this.frameIndex,
      totalReceived: this.totalReceived,
      sentenceNum: this.sentenceNum,
      mapped: this._mappedCount,
    }
  }
}
